import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Drawer as Vaul } from "vaul";
import { cn } from "../lib/cn";
import { useIsMobile } from "../lib/useMedia";
import { IconButton } from "./primitives";

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  /** Force the bottom sheet even on desktop. */
  sheet?: boolean;
}

const MODAL_W = { sm: "sm:max-w-md", md: "sm:max-w-lg", lg: "sm:max-w-2xl", xl: "sm:max-w-4xl" };

/**
 * One dialog API: a centred modal on desktop, a swipe-down bottom sheet on
 * phones (spec: «на мобильном модалки = bottom sheet»).
 */
export function Modal(props: ModalProps) {
  const mobile = useIsMobile();
  return mobile || props.sheet ? <BottomSheet {...props} /> : <CenterModal {...props} />;
}

function CenterModal({ open, onOpenChange, title, description, children, footer, size = "md" }: ModalProps) {
  const { t } = useTranslation();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[200] bg-slate-950/45 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-[201] flex max-h-[min(88dvh,900px)] w-[calc(100%-32px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-3xl border border-p-line bg-p-surface text-p-ink shadow-p-3 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[.97] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-[.97]",
            MODAL_W[size],
          )}
        >
          <div className="flex items-start gap-3 border-b border-p-line px-6 py-4">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[19px] font-bold tracking-tight">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-0.5 text-[14px] text-p-muted">{description}</Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{typeof title === "string" ? title : ""}</Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <IconButton label={t("pro.close")} size="sm" className="-mr-2">
                <X />
              </IconButton>
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
          {footer && <div className="flex justify-end gap-2 border-t border-p-line bg-p-surface-2/50 px-6 py-4">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function BottomSheet({ open, onOpenChange, title, description, children, footer }: ModalProps) {
  return (
    <Vaul.Root open={open} onOpenChange={onOpenChange}>
      <Vaul.Portal>
        <Vaul.Overlay className="fixed inset-0 z-[200] bg-slate-950/45" />
        <Vaul.Content className="fixed inset-x-0 bottom-0 z-[201] flex max-h-[92dvh] flex-col rounded-t-[28px] border-t border-p-line bg-p-surface text-p-ink shadow-p-3 outline-none">
          <div className="mx-auto mt-2.5 h-1.5 w-11 shrink-0 rounded-full bg-p-line-strong" aria-hidden />
          <div className="px-5 pb-2 pt-3">
            <Vaul.Title className="text-[18px] font-bold tracking-tight">{title}</Vaul.Title>
            {description ? (
              <Vaul.Description className="mt-0.5 text-[14px] text-p-muted">{description}</Vaul.Description>
            ) : (
              <Vaul.Description className="sr-only">{typeof title === "string" ? title : ""}</Vaul.Description>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">{children}</div>
          {footer && (
            // Wraps instead of squeezing: with three actions the last (primary) one gets its own full-width row.
            <div className="flex flex-wrap gap-2 border-t border-p-line px-5 pt-3 [&>*]:min-w-[40%] [&>*]:flex-1" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
              {footer}
            </div>
          )}
          {!footer && <div style={{ height: "env(safe-area-inset-bottom)" }} />}
        </Vaul.Content>
      </Vaul.Portal>
    </Vaul.Root>
  );
}

/** Side panel (filters, lead card, notifications). Full screen on phones. */
export function Drawer({
  open,
  onOpenChange,
  title,
  children,
  footer,
  side = "right",
  width = "sm:w-[440px]",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  side?: "right" | "left";
  width?: string;
}) {
  const { t } = useTranslation();
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[200] bg-slate-950/40 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <Dialog.Content
          className={cn(
            "fixed inset-y-0 z-[201] flex w-full flex-col bg-p-surface text-p-ink shadow-p-3 duration-250 data-[state=open]:animate-in data-[state=closed]:animate-out",
            side === "right"
              ? "right-0 data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right sm:border-l"
              : "left-0 data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left sm:border-r",
            "border-p-line",
            width,
          )}
        >
          <div className="flex items-center gap-3 border-b border-p-line px-5 pb-3" style={{ paddingTop: "max(12px, env(safe-area-inset-top))" }}>
            <Dialog.Title className="min-w-0 flex-1 truncate text-[18px] font-bold">{title}</Dialog.Title>
            <Dialog.Description className="sr-only">{typeof title === "string" ? title : ""}</Dialog.Description>
            <Dialog.Close asChild>
              <IconButton label={t("pro.close")} size="md" className="-mr-2">
                <X />
              </IconButton>
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && (
            <div className="flex gap-2 border-t border-p-line px-5 pt-3" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Toast with «Отменить»: the action runs only if the toast isn't undone
 * within `ms` — optimistic UI first, the request after the grace period.
 */
export function toastUndo(message: string, opts: { undoLabel: string; onUndo: () => void; onCommit?: () => void; ms?: number }) {
  let undone = false;
  let committed = false;
  // Leaving the tab or the page must not lose the action (the toast timer
  // pauses in hidden tabs and dies on reload) — commit right away then.
  const onHide = () => {
    if (document.visibilityState === "hidden") commit();
  };
  const cleanup = () => {
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", commit);
  };
  const commit = () => {
    cleanup();
    if (undone || committed) return;
    committed = true;
    opts.onCommit?.();
  };
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", commit);
  const ms = opts.ms ?? 5000;
  toast(message, {
    duration: ms,
    action: {
      label: opts.undoLabel,
      onClick: () => {
        undone = true;
        cleanup();
        opts.onUndo();
      },
    },
    onAutoClose: commit,
    onDismiss: commit,
  });
}

export { toast };
